// The cost-screen "Authorize / Submit" decision, factored out of App.tsx so the
// money-path branching is unit-testable (App.tsx just executes the result).
//
// Five outcomes:
//   advance     — the run already launched and is still live or delivered
//                 (running or run-complete); just move on. A run that
//                 STOPPED (cancelled or failed) does NOT count as launched
//                 here — the caller folds it back to phaseSettled: false so
//                 Back-to-cost-then-Submit relaunches through the normal
//                 arm-confirm/checkout path instead of landing on the dead
//                 run screen (cancel-path fix, 2026-07-04).
//   checkout    — a PAID run, first action: go straight to Stripe Checkout. No
//                 "are you sure" first — Checkout IS the commitment step, and
//                 the run doesn't start until after payment, so the confirm
//                 (about starting the run) would be premature.
//   closed      — a PAID run, but self-serve checkout isn't open (Stripe not
//                 wired, or the operator pulled the public_access switch). Show
//                 a graceful "request access" state instead of an Authorize
//                 button that would 404 against the compound checkout gate.
//   arm-confirm — pilot-free / already-paid: arm the explicit confirm, because
//                 the next click immediately spends the code + starts the ~1hr
//                 run (UX audit F — no silent spend).
//   launch      — confirm already armed → launch the run.

export type CostStepAction =
  | "advance"
  | "checkout"
  | "closed"
  | "arm-confirm"
  | "launch";

export function decideCostStepAction(s: {
  /** True iff a run is genuinely in flight or delivered — phase.kind is
   *  neither "setup" NOR a stopped terminal (cancelled/failed). A stopped
   *  run is over, not settled: the caller computes this as
   *  `phase.kind !== "setup" && !phaseCancelledOrFailed` so a stop can
   *  always be relaunched from this screen, whichever of the two paths
   *  (the stopped panel's own button, or Back-then-Submit) got the user
   *  here. */
  phaseSettled: boolean;
  /** prescan says this run is covered by the free pilot. */
  pilotFreeCovered: boolean;
  /** we've already returned from a successful payment this visit. */
  paidThisVisit: boolean;
  /** the confirm bar is currently armed (confirmLaunch). */
  confirmArmed: boolean;
  /** the server's compound checkout gate (prescan.self_serve_open) — false
   *  means /billing/checkout would 404, so don't route a paid run to it. */
  selfServeOpen: boolean;
}): CostStepAction {
  if (s.phaseSettled) return "advance";
  // Paid run that hasn't been paid this visit needs Stripe — but only when
  // checkout is actually open. When it's closed, surface the request-access
  // state rather than an Authorize that would 404 (also covers the pre-existing
  // "Stripe simply off" case, not just the public_access pull-back).
  // INVARIANT: this is never reached for the by-quote Contact tier — the cost
  // step's continueDisabled blocks its button — so there's deliberately no
  // Contact branch here. A future edit that enables the Contact button must add
  // one (App.tsx's render-side checkoutClosed already excludes Contact).
  if (!s.pilotFreeCovered && !s.paidThisVisit) {
    return s.selfServeOpen ? "checkout" : "closed";
  }
  // Free / already-paid → explicit two-step confirm before the run launches.
  if (!s.confirmArmed) return "arm-confirm";
  return "launch";
}
